/**
 * Unit Locator (UNIT-LOC) — the two marker kinds the console's unit map and
 * log-history view draw, built on the shared HTML-marker factory.
 *
 *   UNIT marker — one flight-intelligence unit at its last known turbine
 *   position. Freshness is encoded TWICE (D26: colour is never the only
 *   signal): fresh = filled `success`, aging = filled `attention`, stale =
 *   hollow ring in `neutral` with a dashed edge. The platform code sits IN
 *   the dot and a three-row tag beside it carries label, tenant and
 *   `serial · platform`, so the map reads without hovering — Christian:
 *   "a unit has four identities — label, tenant, platform, serial".
 *
 *   STAY marker — a numbered circle for a run of flights at one wind farm.
 *   Radius grows with the square root of the flight count so area, not
 *   diameter, tracks the number (a 4× busier stay reads 2× wider, not 4×).
 *   `dim` is the state outside the selected time range.
 *
 * Every colour is a token from `MARKER` (the pinned light set — see
 * `markerMode.ts`); nothing here is a raw design value except the geometry,
 * which is DATA (a count) or a fixed dot size with no token by definition.
 */
import { radius } from "../../theme/tokens/radius.js";
import { shadow } from "../../theme/tokens/shadow.js";
import { spacing } from "../../theme/tokens/spacing.js";
import { typography } from "../../theme/tokens/typography.js";
import { MARKER, NEUTRAL_COLOR } from "./markerMode.js";
import { buildMarkerElement, MARKER_FONT } from "./markerElement.js";

export type UnitFreshness = "fresh" | "aging" | "stale";

/** Dot diameter for a unit marker. Geometry, not a design token. */
export const UNIT_MARKER_SIZE = 16;

export interface UnitMarkerSpec {
  /** The unit's serial — always printed (row 3, and row 1 when unlabelled). */
  serial: string;
  freshness: UnitFreshness;
  /** Operator-given label (row 1). Null → the serial takes row 1. */
  label?: string | null;
  /** Tenant name (row 2). */
  tenantName?: string | null;
  /** Drone platform id as the API gives it (`m400`, `m300-m350`, null). */
  platform?: string | null;
  /** Accessible name; defaults to the serial. */
  accessibleName?: string;
  /** Highlight state (hovered in a linked list). */
  highlighted?: boolean;
}

/** The fill each freshness takes — exported so a legend draws the same. */
export const UNIT_FRESHNESS_COLOR: Record<UnitFreshness, string> = {
  fresh: MARKER.status.success,
  aging: MARKER.status.attention,
  stale: MARKER.status.neutral,
};

/** The short platform code for the tag's ID row: `M400`, `M350` (for the
 *  M300/M350 airframe pair), `?` when the platform is not set. */
export function platformCode(platform: string | null | undefined): string {
  if (!platform) return "?";
  const p = platform.toLowerCase();
  if (p === "m400") return "M400";
  if (p === "m300-m350" || p === "m350" || p === "m300") return "M350";
  return platform.toUpperCase().slice(0, 4);
}

/**
 * A unit has four identities — label, tenant, platform, serial — and the
 * marker shows all four, ranked (Christian, 2026-09-13): the dot stays a
 * plain freshness dot; the tag beside it stacks label (or serial), tenant,
 * and `serial · platform`.
 */
export function buildUnitMarkerElement(
  spec: UnitMarkerSpec,
): HTMLButtonElement {
  const code = platformCode(spec.platform);
  const el = buildMarkerElement(
    {
      color: UNIT_FRESHNESS_COLOR[spec.freshness],
      sizePx: UNIT_MARKER_SIZE,
    },
    spec.accessibleName ?? spec.serial,
    { className: "unit-marker", testId: "unit-marker" },
  );
  el.setAttribute("data-freshness", spec.freshness);
  el.setAttribute("data-serial", spec.serial);
  el.setAttribute("data-platform", code);
  // The factory centres its content; the tag hangs OFF the dot to the
  // right, so the button must not clip it.
  el.style.overflow = "visible";
  if (spec.freshness === "stale") {
    // Hollow + dashed: a unit we have not heard from in a long while is an
    // outline of where it was, not a solid presence.
    el.style.background = MARKER.surface.paper;
    el.style.border = `2px dashed ${MARKER.status.neutral}`;
  }
  if (spec.highlighted) {
    el.style.boxShadow = `0 0 0 3px ${MARKER.primary}, ${shadow.darkXs}`;
  }

  const tag = document.createElement("span");
  tag.className = "unit-marker-tag";
  tag.style.cssText = [
    "position:absolute",
    `left:${String(UNIT_MARKER_SIZE + spacing.xs)}px`,
    "top:50%",
    "transform:translateY(-50%)",
    "display:flex",
    "flex-direction:column",
    "align-items:flex-start",
    "gap:1px",
    "white-space:nowrap",
    "text-align:left",
    `font-family:${typography.fontFamily}`,
    "line-height:1.2",
    // A tag is a light card on the map: primary text on the page-white
    // ground with the subtle hairline — the Fleet Map's year-tag recipe,
    // grown to three rows.
    `color:${spec.freshness === "stale" ? MARKER.text.secondary : MARKER.text.primary}`,
    `background:${MARKER.surface.paper}`,
    `border:1px solid ${MARKER.borders.subtle}`,
    `border-radius:${String(radius.control)}px`,
    `padding:${String(spacing.xs)}px ${String(spacing.xs + 2)}px`,
    "pointer-events:none",
  ].join(";");

  const row1 = document.createElement("span");
  row1.className = "unit-marker-serial";
  row1.textContent = spec.label ?? spec.serial;
  row1.style.cssText = `font:${String(typography.weight.semibold)} ${String(
    typography.size["caption-sm"],
  )}px/1.2 ${typography.fontFamily}`;
  tag.appendChild(row1);

  if (spec.tenantName) {
    const row2 = document.createElement("span");
    row2.className = "unit-marker-tenant";
    row2.textContent = spec.tenantName;
    row2.style.cssText = `font:${String(typography.weight.regular)} ${String(
      typography.size["label-md"],
    )}px/1.2 ${typography.fontFamily}`;
    tag.appendChild(row2);
  }

  const row3 = document.createElement("span");
  row3.className = "unit-marker-id";
  row3.textContent = `${spec.serial} · ${code}`;
  // Mono 10px, one below label-md: an ID line, not a label (D6).
  row3.style.cssText = [
    `font:${String(typography.weight.regular)} 10px/1.2 ${typography.fontFamilyMono}`,
    `color:${MARKER.text.secondary}`,
    "letter-spacing:0.02em",
  ].join(";");
  tag.appendChild(row3);

  el.appendChild(tag);
  return el;
}

export type FlightDotState = "plain" | "hovered" | "selected";

export interface FlightDotSpec {
  /** plain = one flight of the hovered stay (small, neutral); hovered = the
   *  flight behind the hovered table row; selected = a flight the operator
   *  ticked in the table — a PERSISTENT mark that survives the hover, so a
   *  selection made while scrolled into the table is still there after
   *  scrolling back up to the map (Christian, 2026-09-14). */
  state: FlightDotState;
  accessibleName: string;
}

/** Diameter per state. Geometry, not a design token. */
export const FLIGHT_DOT_SIZE: Record<FlightDotState, number> = {
  plain: 6,
  hovered: 12,
  selected: 14,
};

/**
 * A single flight on the map — a position we HOLD A LOG FOR, so it is drawn
 * in the accent at every size. It used to be neutral when plain, which was
 * fine while nothing else on the map was grey; `buildTurbineDotElement` now
 * claims grey for "a turbine we merely know about", and the whole point of
 * the pair is that an operator reads coverage at a glance (Christian,
 * 2026-09-15: "small grey dots ... where we have wind turbines, and small
 * green dots where we actually have log files"). `selected` keeps its paper
 * gap and accent ring — two signals besides size (D26) — because unlike the
 * hover it is meant to stay on screen.
 */
export function buildFlightDotElement(spec: FlightDotSpec): HTMLButtonElement {
  const testId =
    spec.state === "plain" ? "flight-dot" : `flight-dot-${spec.state}`;
  const el = buildMarkerElement(
    {
      color: MARKER.primary,
      sizePx: FLIGHT_DOT_SIZE[spec.state],
    },
    spec.accessibleName,
    { className: `flight-dot flight-dot-${spec.state}`, testId },
  );
  el.setAttribute("data-state", spec.state);
  if (spec.state === "selected") {
    el.setAttribute("data-selected", "true");
    el.style.boxShadow = `0 0 0 2px ${MARKER.surface.paper}, 0 0 0 4px ${MARKER.primary}, ${shadow.darkXs}`;
    el.style.cursor = "pointer";
  }
  return el;
}

export interface TurbineDotSpec {
  /** Accessible name, e.g. "Turbine A12 · Adorf II". */
  accessibleName: string;
  /** Native tooltip. Defaults to the accessible name. */
  title?: string;
}

/** Smaller than the smallest flight: a turbine we know of must never outweigh
 *  a flight we hold. */
export const TURBINE_DOT_SIZE = 5;

/**
 * A turbine the platform knows stands at this point — grey, hairline-ringed,
 * unelevated. Drawn UNDER the flights so a park reads as its full set of
 * positions with the ones we hold logs for lit up on top; the gap between the
 * two is the inspection coverage, and the reason this marker exists.
 *
 * Not interactive: it carries a `title` for the pointer and an `aria-label`
 * for the reader, but no handler — the stays and the flights are what a
 * click means on this map.
 */
export function buildTurbineDotElement(
  spec: TurbineDotSpec,
): HTMLButtonElement {
  const el = buildMarkerElement(
    { color: NEUTRAL_COLOR, sizePx: TURBINE_DOT_SIZE },
    spec.accessibleName,
    { className: "turbine-dot", testId: "turbine-dot" },
  );
  // A 2px ring and an elevation on a 5px dot would read as a control. The
  // hairline keeps it legible on tiles without competing with the flights.
  el.style.border = `1px solid ${MARKER.surface.paper}`;
  el.style.boxShadow = "none";
  el.style.cursor = "default";
  el.title = spec.title ?? spec.accessibleName;
  return el;
}

export interface StayMarkerSpec {
  /** 1-based chronological index of the stay. */
  index: number;
  /** Flights in the stay — drives the radius. */
  count: number;
  /** Outside the selected range. */
  dim?: boolean;
  highlighted?: boolean;
  /** Accessible name, e.g. "Stay 3 · Pencloe wind farm · 12 flights". */
  accessibleName: string;
}

/** Stay diameter from the flight count: area tracks the count. */
export function stayMarkerSize(count: number): number {
  const n = Math.max(1, count);
  return Math.round(UNIT_MARKER_SIZE + 4 + Math.sqrt(n) * 4.4);
}

export function buildStayMarkerElement(
  spec: StayMarkerSpec,
): HTMLButtonElement {
  const el = buildMarkerElement(
    {
      color: spec.dim ? NEUTRAL_COLOR : MARKER.primary,
      sizePx: stayMarkerSize(spec.count),
      label: String(spec.index),
    },
    spec.accessibleName,
    { className: "stay-marker", testId: "stay-marker" },
  );
  el.setAttribute("data-stay-index", String(spec.index));
  el.setAttribute("data-dim", spec.dim ? "true" : "false");
  el.style.font = MARKER_FONT;
  if (spec.dim) el.style.opacity = "0.6";
  if (spec.highlighted) {
    el.style.boxShadow = `0 0 0 3px ${MARKER.text.primary}, ${shadow.darkXs}`;
  }
  return el;
}
